export interface InvoiceItem {
  id: string;
  type: 'plan_mensual' | 'licencia_anual' | 'frecuencia_preferida' | 'dominio_premium' | 'storage_extra' | 'app_android' | 'app_iphone' | 'app_smarttv' | 'setup_fee' | 'soporte_prioritario';
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

export const invoiceItemTypeLabels: Record<InvoiceItem['type'], string> = {
  plan_mensual: 'Plan mensual',
  licencia_anual: 'Licencia anual',
  frecuencia_preferida: 'Frecuencia preferida',
  dominio_premium: 'Dominio premium',
  storage_extra: 'Storage extra',
  app_android: 'App Android',
  app_iphone: 'App iPhone',
  app_smarttv: 'App Smart TV',
  setup_fee: 'Setup fee',
  soporte_prioritario: 'Soporte prioritario',
};

export interface Invoice {
  id: number;
  number: string;
  radioName: string;
  frequency: string;
  planName: string;
  concepto: string;
  items: InvoiceItem[];
  subtotal: number;
  discount: number;
  taxRate: number;
  tax: number;
  total: number;
  status: 'draft' | 'pending' | 'paid' | 'overdue' | 'cancelled' | 'refunded';
  issuedAt: string;
  dueDate: string;
  paidAt: string | null;
  paymentId?: string;
  contractId?: string;
  notes?: string;
}

export const invoiceStatusLabels: Record<Invoice['status'], string> = {
  draft: 'Borrador',
  pending: 'Pendiente',
  paid: 'Pagada',
  overdue: 'Vencida',
  cancelled: 'Cancelada',
  refunded: 'Reembolsada',
};

export const invoiceStatusColors: Record<Invoice['status'], string> = {
  draft: 'bg-foreground-100 text-foreground-600',
  pending: 'bg-amber-50 text-amber-600',
  paid: 'bg-emerald-50 text-emerald-600',
  overdue: 'bg-red-50 text-red-500',
  cancelled: 'bg-foreground-100 text-foreground-500',
  refunded: 'bg-sky-50 text-sky-600',
};

// Helpers

const planPrices: Record<string, number> = { Starter: 0, Básico: 9, Pro: 19, Business: 39, Premium: 49 };
const planConceptos: Record<string, string> = {
  Starter: 'Plan Starter — Acceso básico',
  Básico: 'Plan Básico — Estándar',
  Pro: 'Plan Pro — Profesional',
  Business: 'Plan Business — Empresarial',
  Premium: 'Plan Premium — Completo',
};

function seq(): string {
  return Math.random().toString(36).slice(2, 8);
}

function makeItems(planName: string, opts: {
  licencia?: boolean;
  frecuencia?: number;
  dominio?: number;
  storage?: number;
  appAndroid?: number;
  appIphone?: number;
  appSmarttv?: number;
  setupFee?: number;
  soporte?: number;
}): InvoiceItem[] {
  const items: InvoiceItem[] = [];
  const planPrice = planPrices[planName] ?? 19;

  items.push({
    id: `it-${seq()}`, type: 'plan_mensual',
    description: `Plan ${planName}`,
    quantity: 1, unitPrice: planPrice, total: planPrice,
  });

  if (opts.licencia) {
    items.push({
      id: `it-${seq()}`, type: 'licencia_anual',
      description: 'Licencia anual de frecuencia RFW',
      quantity: 1, unitPrice: 49.95, total: 49.95,
    });
  }

  if (opts.frecuencia && opts.frecuencia > 0) {
    items.push({
      id: `it-${seq()}`, type: 'frecuencia_preferida',
      description: 'Frecuencia preferida — Posicionamiento prioritario',
      quantity: 1, unitPrice: opts.frecuencia, total: opts.frecuencia,
    });
  }

  if (opts.dominio && opts.dominio > 0) {
    items.push({
      id: `it-${seq()}`, type: 'dominio_premium',
      description: 'Dominio premium — .com / .fm primer año',
      quantity: 1, unitPrice: opts.dominio, total: opts.dominio,
    });
  }

  if (opts.storage && opts.storage > 0) {
    items.push({
      id: `it-${seq()}`, type: 'storage_extra',
      description: 'Storage extra — Almacenamiento adicional',
      quantity: 1, unitPrice: opts.storage, total: opts.storage,
    });
  }

  if (opts.appAndroid && opts.appAndroid > 0) {
    items.push({
      id: `it-${seq()}`, type: 'app_android',
      description: 'App Android — Desarrollo y publicación',
      quantity: 1, unitPrice: opts.appAndroid, total: opts.appAndroid,
    });
  }

  if (opts.appIphone && opts.appIphone > 0) {
    items.push({
      id: `it-${seq()}`, type: 'app_iphone',
      description: 'App iPhone — Desarrollo y publicación',
      quantity: 1, unitPrice: opts.appIphone, total: opts.appIphone,
    });
  }

  if (opts.appSmarttv && opts.appSmarttv > 0) {
    items.push({
      id: `it-${seq()}`, type: 'app_smarttv',
      description: 'App Smart TV — Samsung / LG / Android TV',
      quantity: 1, unitPrice: opts.appSmarttv, total: opts.appSmarttv,
    });
  }

  if (opts.setupFee && opts.setupFee > 0) {
    items.push({
      id: `it-${seq()}`, type: 'setup_fee',
      description: 'Setup fee — Configuración y migración inicial',
      quantity: 1, unitPrice: opts.setupFee, total: opts.setupFee,
    });
  }

  if (opts.soporte && opts.soporte > 0) {
    items.push({
      id: `it-${seq()}`, type: 'soporte_prioritario',
      description: 'Soporte prioritario 24/7',
      quantity: 1, unitPrice: opts.soporte, total: opts.soporte,
    });
  }

  return items;
}

function calcTotals(items: InvoiceItem[], discount: number, taxRate: number) {
  const subtotal = items.reduce((s, i) => s + i.total, 0);
  const afterDiscount = Math.max(0, subtotal - discount);
  const tax = Math.round(afterDiscount * taxRate * 100) / 100;
  const total = afterDiscount + tax;
  return { subtotal, tax, total };
}

// Invoices

export const invoices: Invoice[] = [
  // 1 — Plan Pro + Licencia anual + Frecuencia preferida
  (() => {
    const plan = 'Pro';
    const items = makeItems(plan, { licencia: true, frecuencia: 99 });
    const t = calcTotals(items, 0, 0.16);
    return { id: 1, number: 'INV-2026-0001', radioName: 'Stereo Visión', frequency: '94.70 RFW', planName: plan, concepto: planConceptos[plan], items, ...t, discount: 0, status: 'paid' as const, issuedAt: '2026-06-01', dueDate: '2026-06-15', paidAt: '2026-06-01', paymentId: 'PAY-20260601-001', contractId: 'CTR-2026-001', notes: 'Pago puntual con Stripe. Cliente premium.' };
  })(),

  // 2 — Premium + Licencia + Dominio
  (() => {
    const plan = 'Premium';
    const items = makeItems(plan, { licencia: true, dominio: 39 });
    const t = calcTotals(items, 0, 0.16);
    return { id: 2, number: 'INV-2026-0002', radioName: 'Radio Nueva Actitud', frequency: '87.90 RFW', planName: plan, concepto: planConceptos[plan], items, ...t, discount: 0, status: 'paid' as const, issuedAt: '2026-06-01', dueDate: '2026-06-15', paidAt: '2026-06-02', paymentId: 'PAY-20260601-002', contractId: 'CTR-2026-002' };
  })(),

  // 3 — Pro + Licencia
  (() => {
    const plan = 'Pro';
    const items = makeItems(plan, { licencia: true });
    const t = calcTotals(items, 0, 0.16);
    return { id: 3, number: 'INV-2026-0003', radioName: 'TechBeats', frequency: '88.50 RFW', planName: plan, concepto: planConceptos[plan], items, ...t, discount: 0, status: 'paid' as const, issuedAt: '2026-06-02', dueDate: '2026-06-16', paidAt: '2026-06-02', paymentId: 'PAY-20260602-003', contractId: 'CTR-2026-003' };
  })(),

  // 4 — Business + Licencia + Apps Android + iPhone
  (() => {
    const plan = 'Business';
    const items = makeItems(plan, { licencia: true, appAndroid: 79, appIphone: 79 });
    const t = calcTotals(items, 0, 0.16);
    return { id: 4, number: 'INV-2026-0004', radioName: 'Radio Élite Digital', frequency: '91.30 RFW', planName: plan, concepto: planConceptos[plan], items, ...t, discount: 0, status: 'paid' as const, issuedAt: '2026-06-02', dueDate: '2026-06-16', paidAt: '2026-06-03', paymentId: 'PAY-20260602-004', contractId: 'CTR-2026-004' };
  })(),

  // 5 — Pro + Licencia + Frecuencia + Dominio
  (() => {
    const plan = 'Pro';
    const items = makeItems(plan, { licencia: true, frecuencia: 79, dominio: 35 });
    const t = calcTotals(items, 0, 0.16);
    return { id: 5, number: 'INV-2026-0005', radioName: 'Radio Deportiva Total', frequency: '101.70 RFW', planName: plan, concepto: planConceptos[plan], items, ...t, discount: 0, status: 'paid' as const, issuedAt: '2026-06-03', dueDate: '2026-06-17', paidAt: '2026-06-04', paymentId: 'PAY-20260603-005', contractId: 'CTR-2026-005' };
  })(),

  // 6 — Premium + Licencia + Frecuencia + Storage + Setup
  (() => {
    const plan = 'Premium';
    const items = makeItems(plan, { licencia: true, frecuencia: 49, storage: 14.99, setupFee: 29 });
    const t = calcTotals(items, 0, 0.16);
    return { id: 6, number: 'INV-2026-0006', radioName: 'Radio Urbana 101', frequency: '103.50 RFW', planName: plan, concepto: planConceptos[plan], items, ...t, discount: 0, status: 'paid' as const, issuedAt: '2026-06-03', dueDate: '2026-06-17', paidAt: '2026-06-04', paymentId: 'PAY-20260603-006', contractId: 'CTR-2026-006' };
  })(),

  // 7 — Business + Licencia + Frecuencia + Apps Android + iPhone + Dominio
  (() => {
    const plan = 'Business';
    const items = makeItems(plan, { licencia: true, frecuencia: 129, appAndroid: 59, appIphone: 59, dominio: 49 });
    const t = calcTotals(items, 10, 0.16);
    return { id: 7, number: 'INV-2026-0007', radioName: 'Radio Éxitos TV', frequency: '100.00 RFW', planName: plan, concepto: planConceptos[plan], items, ...t, discount: 10, status: 'paid' as const, issuedAt: '2026-06-04', dueDate: '2026-06-18', paidAt: '2026-06-05', paymentId: 'PAY-20260604-007', contractId: 'CTR-2026-007' };
  })(),

  // 8 — Básico + Licencia → pending
  (() => {
    const plan = 'Básico';
    const items = makeItems(plan, { licencia: true });
    const t = calcTotals(items, 0, 0.16);
    return { id: 8, number: 'INV-2026-0008', radioName: 'Radio Folclor Andino', frequency: '107.90 RFW', planName: plan, concepto: planConceptos[plan], items, ...t, discount: 0, status: 'pending' as const, issuedAt: '2026-06-05', dueDate: '2026-06-19', paidAt: null, contractId: 'CTR-2026-008' };
  })(),

  // 9 — Pro + Licencia → overdue
  (() => {
    const plan = 'Pro';
    const items = makeItems(plan, { licencia: true });
    const t = calcTotals(items, 0, 0.16);
    return { id: 9, number: 'INV-2026-0009', radioName: 'Jazz Club Radio', frequency: '92.80 RFW', planName: plan, concepto: planConceptos[plan], items, ...t, discount: 0, status: 'overdue' as const, issuedAt: '2026-05-01', dueDate: '2026-05-15', paidAt: null, paymentId: 'PAY-20260605-009', contractId: 'CTR-2026-009', notes: 'Cliente no responde a recordatorios. Tarjeta rechazada 3 veces.' };
  })(),

  // 10 — Starter → solo plan
  (() => {
    const plan = 'Starter';
    const items = makeItems(plan, {});
    const t = calcTotals(items, 0, 0.16);
    return { id: 10, number: 'INV-2026-0010', radioName: 'Radio Marítima', frequency: '89.50 RFW', planName: plan, concepto: planConceptos[plan], items, ...t, discount: 0, status: 'paid' as const, issuedAt: '2026-06-01', dueDate: '2026-06-15', paidAt: '2026-06-01', contractId: 'CTR-2026-010' };
  })(),

  // 11 — Starter + Licencia → pending
  (() => {
    const plan = 'Starter';
    const items = makeItems(plan, { licencia: true });
    const t = calcTotals(items, 0, 0.16);
    return { id: 11, number: 'INV-2026-0011', radioName: 'Radio Sonora Digital', frequency: '96.20 RFW', planName: plan, concepto: planConceptos[plan], items, ...t, discount: 0, status: 'pending' as const, issuedAt: '2026-06-06', dueDate: '2026-06-20', paidAt: null, contractId: 'CTR-2026-011', notes: 'Pago con cheque en proceso de verificación.' };
  })(),

  // 12 — Básico + Licencia + Setup + Soporte → refunded
  (() => {
    const plan = 'Básico';
    const items = makeItems(plan, { licencia: true, setupFee: 25, soporte: 19.99 });
    const t = calcTotals(items, 0, 0.16);
    return { id: 12, number: 'INV-2026-0012', radioName: 'Clásica FM', frequency: '99.10 RFW', planName: plan, concepto: planConceptos[plan], items, ...t, discount: 0, status: 'refunded' as const, issuedAt: '2026-05-28', dueDate: '2026-06-11', paidAt: '2026-05-29', paymentId: 'PAY-20260528-012', contractId: 'CTR-2026-012', notes: 'Reembolsada por cierre definitivo de la emisora.' };
  })(),

  // 13 — Premium + Licencia + Frecuencia + App Smart TV + Soporte → overdue
  (() => {
    const plan = 'Premium';
    const items = makeItems(plan, { licencia: true, frecuencia: 89, appSmarttv: 49, soporte: 39.99 });
    const t = calcTotals(items, 0, 0.16);
    return { id: 13, number: 'INV-2026-0013', radioName: 'Radio Retro Hits', frequency: '93.10 RFW', planName: plan, concepto: planConceptos[plan], items, ...t, discount: 0, status: 'overdue' as const, issuedAt: '2026-05-07', dueDate: '2026-05-21', paidAt: null, paymentId: 'PAY-20260607-013', contractId: 'CTR-2026-013', notes: 'Fondos insuficientes reiterados. Se intentó cobro 3 veces.' };
  })(),

  // 14 — Business + Licencia + Dominio + Storage → pending
  (() => {
    const plan = 'Business';
    const items = makeItems(plan, { licencia: true, dominio: 55, storage: 19.99 });
    const t = calcTotals(items, 0, 0.16);
    return { id: 14, number: 'INV-2026-0014', radioName: 'Radio Vibra FM', frequency: '90.80 RFW', planName: plan, concepto: planConceptos[plan], items, ...t, discount: 0, status: 'pending' as const, issuedAt: '2026-06-08', dueDate: '2026-06-22', paidAt: null, paymentId: 'PAY-20260608-014', contractId: 'CTR-2026-014' };
  })(),

  // 15 — Básico + Licencia + App iPhone + Dominio → pending
  (() => {
    const plan = 'Básico';
    const items = makeItems(plan, { licencia: true, appIphone: 39, dominio: 29 });
    const t = calcTotals(items, 0, 0.16);
    return { id: 15, number: 'INV-2026-0015', radioName: 'Radio Fusión Latina', frequency: '105.30 RFW', planName: plan, concepto: planConceptos[plan], items, ...t, discount: 0, status: 'pending' as const, issuedAt: '2026-06-09', dueDate: '2026-06-23', paidAt: null, contractId: 'CTR-2026-015', notes: 'Registrada manualmente. Cliente nuevo.' };
  })(),

  // 16 — Pro + Licencia + Dominio → cancelled
  (() => {
    const plan = 'Pro';
    const items = makeItems(plan, { licencia: true, dominio: 35 });
    const t = calcTotals(items, 0, 0.16);
    return { id: 16, number: 'INV-2026-0016', radioName: 'Radio Horizonte Digital', frequency: '102.10 RFW', planName: plan, concepto: planConceptos[plan], items, ...t, discount: 0, status: 'cancelled' as const, issuedAt: '2026-06-06', dueDate: '2026-06-20', paidAt: null, contractId: 'CTR-2026-016', notes: 'Cancelada a solicitud de la emisora antes del cobro.' };
  })(),

  // 17 — Business + Licencia + Frecuencia + Apps Android/iPhone/SmartTV + Dominio + Storage + Soporte → draft (full package)
  (() => {
    const plan = 'Business';
    const items = makeItems(plan, { licencia: true, frecuencia: 149, appAndroid: 99, appIphone: 99, appSmarttv: 89, dominio: 59, storage: 24.99, soporte: 45 });
    const t = calcTotals(items, 25, 0.16);
    return { id: 17, number: 'INV-2026-0017', radioName: 'Radio Sinfonía Web', frequency: '95.40 RFW', planName: plan, concepto: planConceptos[plan], items, ...t, discount: 25, status: 'draft' as const, issuedAt: '2026-06-10', dueDate: '2026-06-24', paidAt: null, contractId: 'CTR-2026-017', notes: 'Borrador — paquete completo pendiente de revisión por el equipo SA.' };
  })(),

  // 18 — Premium + Licencia + Frecuencia + Dominio → refunded
  (() => {
    const plan = 'Premium';
    const items = makeItems(plan, { licencia: true, frecuencia: 109, dominio: 45 });
    const t = calcTotals(items, 0, 0.16);
    return { id: 18, number: 'INV-2026-0018', radioName: 'Radio Cumbia Total', frequency: '86.70 RFW', planName: plan, concepto: planConceptos[plan], items, ...t, discount: 0, status: 'refunded' as const, issuedAt: '2026-05-15', dueDate: '2026-05-29', paidAt: '2026-05-16', paymentId: 'PAY-20260515-018', contractId: 'CTR-2026-018', notes: 'Reembolsada: cheque sin fondos del cliente.' };
  })(),
];